/**
 * Strategy r684 = r643 + xStocks (backtest `r684_s28_stocks`), stocks admitted by trusted issuer
 * instead of a mint list: every rule number the autopilot uses. Percent and ratio values are exact
 * decimal strings; durations are whole seconds or minutes.
 */
export const STRATEGY_RULES_VERSION = 5;
function deepFreeze(value) {
    for (const nested of Object.values(value))
        if (typeof nested === "object" && nested !== null)
            deepFreeze(nested);
    return Object.freeze(value);
}
export const STRATEGY = deepFreeze({
    pool: {
        minTvlUsd: "1000000",
        minPoolAgeSeconds: 86_400,
        maxPriceAgeSeconds: 600,
        /** Orca's full-range-only tick spacing: such a pool cannot hold a concentrated range. */
        fullRangeTickSpacing: 32_896,
        maxEntryFeePercent: "1",
        minEffectiveLps: "2",
        /**
         * Tokenized stocks of trusted issuers: issuer-controlled tokens admitted despite their issuer
         * controls, with their own TVL floor. Every other issuer-controlled mint is still refused.
         */
        issuerStocks: {
            minTvlUsd: "100000",
            /**
             * The most the in-force Scaled UI multiplier may differ from 1: the entry gate reads their
             * price history as raw pool units, so this caps that history's frame error.
             */
            maxMultiplierDeviation: "0.02",
            /**
             * Vetted once at addition (docs/issuers.md). The mint authority is cross-checked against the
             * issuer's own list (`source`), never trusted alone: anyone can copy it onto a fake mint.
             */
            issuers: [
                {
                    id: "xstocks",
                    name: "xStocks",
                    mintAuthority: "7pt9tkctJPK7PPNQJ77GKg8ZffSF6QxoMiCFYHxrtaCj",
                    source: "backed-list",
                },
                {
                    id: "ondo",
                    name: "Ondo",
                    mintAuthority: "9foMHsSDq7nMg4WPusSz9eY7tyxyukqborA8GyU5cUxD",
                    source: "jupiter-verified",
                },
            ],
            /** Cut from the name before matching: bond durations, not leverage ("Ultra-Short Income"). */
            nameTermExceptions: ["ultra-short", "ultra short"],
            /** Whole-word, case-insensitive name terms excluding a stock: leveraged, inverse, pre-IPO. */
            excludedNameTerms: [
                "1.5x",
                "2x",
                "3x",
                "4x",
                "5x",
                "short",
                "inverse",
                "leveraged",
                "leverage",
                "ultrapro",
                "bear",
                "pre-ipo",
                "preipo",
            ],
            /** Leveraged, inverse and volatility funds whose names do not say so ("TQQQ xStock"). */
            excludedUnderlyings: [
                "TQQQ",
                "SQQQ",
                "QLD",
                "QID",
                "SSO",
                "SDS",
                "UPRO",
                "SPXU",
                "SPXL",
                "SPXS",
                "SH",
                "PSQ",
                "DOG",
                "TNA",
                "TZA",
                "SOXL",
                "SOXS",
                "TECL",
                "TECS",
                "FNGU",
                "FNGD",
                "LABU",
                "LABD",
                "TSLL",
                "TSLQ",
                "NVDL",
                "NVDQ",
                "UVXY",
                "UVIX",
                "SVIX",
                "SVXY",
                "VXX",
            ],
        },
    },
    gate: {
        shortWindowMinutes: 30,
        baselineWindowMinutes: 1_440,
        meanWindowMinutes: 30,
        metricDecimals: 8,
        volSkipRatio: "1.5",
        distanceSkip: "2",
        trendSkip: "1.5",
        volElevatedRatio: "1.2",
        distanceCaution: "1",
        trendSkew: "0.5",
        normalWidthPercent: "20",
        elevatedWidthPercent: "25",
        skewSharePercent: "70",
        reentryMinSeconds: 600,
        reentryTrend: "0.5",
        reentryDistance: "1",
        /** `gateWaitUntil = now + waitSeconds` after a skip (skip policy "wait"). */
        waitSeconds: 1_800,
        /** Live-only rescan when no pool passes the filters; the backtest would wait for a close. */
        noCandidateRescanSeconds: 1_800,
    },
    sizing: {
        maxLiquiditySharePercent: "2",
        shareShrinkStepPercent: "1",
        shareShrinkMaxSteps: 3,
        minEntryUsd: "50",
        minSolReserveLamports: "20000000",
    },
    exits: {
        stopDistancePercent: "5",
        profitStopDistancePercent: "0",
        profitStopThresholdPercent: "1",
        outOfRangeCloseSeconds: 7_200,
        /** H1: a position this old whose fees per day held stay under the percent closes to USDC. */
        lowYieldExitAfterSeconds: 432_000,
        lowYieldExitPercentPerDay: "0.1",
    },
    /** Concurrent positions a run may hold. */
    maxPositions: 20,
    /** C5: entries halve while the trailing week lost `weekLossPercent` or more. */
    regime: {
        weekLossPercent: "5",
        weekSeconds: 604_800,
        sampleSeconds: 600,
        multiplier: "0.5",
    },
});
/** JSON with object keys sorted at every depth, so the hash does not depend on literal order. */
function stableJson(value) {
    if (Array.isArray(value))
        return `[${value.map(stableJson).join(",")}]`;
    if (typeof value === "object" && value !== null)
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
            .join(",")}}`;
    return JSON.stringify(value);
}
/**
 * FNV-1a 64 over the stable JSON. Core runs in the browser too, where `node:crypto` is absent and
 * WebCrypto is async; the hash only detects a rules change between Start and execution, it guards
 * against no adversary, so a non-cryptographic hash is enough.
 */
function fnv1a64(text) {
    let hash = 0xcbf29ce484222325n;
    for (const byte of new TextEncoder().encode(text)) {
        hash ^= BigInt(byte);
        hash = (hash * 0x100000001b3n) & 0xffffffffffffffffn;
    }
    return hash.toString(16).padStart(16, "0");
}
/** The hash a run's approval seals: the rules version and every rule number. */
export function strategyRulesHash(version, rules) {
    return fnv1a64(stableJson({ version, rules }));
}
export const STRATEGY_RULES_HASH = strategyRulesHash(STRATEGY_RULES_VERSION, STRATEGY);
/**
 * Hashes of earlier rules the current ones supersede compatibly, by their version: a run sealed
 * under one keeps its authority and runs under the current rules (v3 = v2 + the low-yield exit,
 * v4 = v3 + xStocks by a mint list, v5 = v4 with trusted issuers instead of that list).
 */
export const STRATEGY_RULES_UPGRADES = Object.freeze({
    a68118997fcf43a2: 2,
    "3764f8ea1ba0a9e8": 3,
    "8c0473a265730a75": 4,
});
/** A run sealed under `hash` runs under the current rules. */
export const rulesCurrent = (hash) => hash === STRATEGY_RULES_HASH || Object.hasOwn(STRATEGY_RULES_UPGRADES, hash);
